/**
 * Client-side prediction for the local player.
 *
 * Waiting for a 30 Hz server to confirm a move means every click costs at least a
 * tick before anything happens, which reads as lag no amount of smoothing hides.
 * Instead the client runs the server's own movement rule immediately, at full
 * frame rate, and corrects itself when the authoritative position arrives.
 *
 * Pure: no Babylon, no clock. Walkability is injected, because only the zone
 * knows it.
 */

export interface Point {
  x: number;
  z: number;
}

/** How far the predicted position may drift before it is corrected at all. */
export const RECONCILE_TOLERANCE = 0.12;

/**
 * Beyond this the prediction is not a small error, it is wrong — knocked back,
 * stopped by something the client did not know about, or teleported by a zone
 * change. Snap rather than slide.
 */
export const RECONCILE_SNAP_DISTANCE = 3.5;

/** How fast a correction is absorbed, in e-foldings per second. */
export const RECONCILE_RATE = 9;

/**
 * One frame of a ramped walk: how far to move, and the new ramp age.
 *
 * **The same rule as `walkRampStep` in `grind/internal/game/movement.go`, and it has
 * to be.** Walking is the movement a player spends the whole game doing, so a
 * divergence here is a correction on every frame of it — a far worse failure than the
 * occasional snap a mispredicted dodge costs. It is the third pairing of this kind,
 * beside `tryMove`/`stepToward` and `advanceDodge`/`stepDodge`.
 *
 * ── Why an age and not a velocity ──────────────────────────────────────────────
 *
 * `ramp` is *seconds into the ramp*, in `[0, rampTime]`, and the speed at that age is
 * `speed × ramp / rampTime`. The distance is integrated **analytically** rather than
 * by multiplying a velocity by `dt`: the ground covered while the age goes `r0 → r1`
 * is exactly `speed × (r1² − r0²) / (2·rampTime)`, and those terms telescope. So a
 * walk sliced into the server's 30 ticks a second and the same walk sliced into 240
 * display frames end up in the *same place*, to floating point.
 *
 * A velocity stepped with Euler would not: the 30 Hz half would fall ≈0.07 units
 * behind the 240 Hz half on every launch — over half of `RECONCILE_TOLERANCE`, given
 * away for free, on the most common action in the game.
 *
 * ── Why the brake is measured in distance ─────────────────────────────────────
 *
 * `brake` is the ramp age from which unwinding all the way to zero covers exactly
 * `remaining`, so a walk eases into its destination and lands *on* it rather than
 * overshooting and being clamped back. Above that age the walk cannot stop in time and
 * unwinds; below it, it winds up — but never past `brake`, or it would commit to a
 * speed it cannot shed before it arrives.
 *
 * The clamp down to `brake` on entry is load-bearing: without it the frame that crosses
 * the threshold at full speed would be handed the whole braking distance as one step —
 * three times walking pace in a single frame. A brake that opens with a lurch is worse
 * than no brake.
 *
 * Returned as a tuple rather than mutating, so it can be tested without a predictor.
 */
export function walkRampStep(
  ramp: number,
  dt: number,
  speed: number,
  remaining: number,
  rampTime: number,
): [step: number, next: number] {
  if (!(dt > 0) || !(speed > 0)) return [0, ramp];
  // A league may turn the ramp off. Instant full speed, and the age is meaningless.
  if (!(rampTime > 0)) return [speed * dt, 0];
  if (!(remaining > 0)) return [0, 0];

  const brake = Math.min(Math.sqrt((2 * rampTime * remaining) / speed), rampTime);

  // `>=` rather than `>`, and only once the brake age is short of a full ramp. The two
  // conditions together are what make the brake an exact fixed point: a walk longer than
  // the braking distance has `brake === rampTime` and must *cruise* at the top of the
  // ramp rather than unwind, while a walk sitting exactly on its braking age must unwind
  // rather than coast — coasting there is a step the remaining distance cannot pay for,
  // and the clamp that saves it leaves an age behind.
  if (brake < rampTime && ramp >= brake) {
    // Braking, from what the remaining distance actually supports.
    const to = brake - dt;
    // The last frame of a brake. Hand back the remaining distance itself rather than
    // recomputing it from a square root that has already been rounded once, so the walk
    // arrives exactly and `stepToward` snaps.
    if (to < RAMP_EPSILON) return [remaining, 0];
    return [(speed * (brake * brake - to * to)) / (2 * rampTime), to];
  }

  // Winding up, but never past the age the remaining distance can still stop from.
  const to = Math.min(ramp + dt, brake);
  const ramping = to - ramp;
  // The ramping part of the frame, exactly; then whatever is left of the frame at the
  // fraction reached. When `brake` is `rampTime` — every walk longer than the braking
  // distance — that second term is plain cruising at full speed.
  const step = speed * ((to * to - ramp * ramp) / (2 * rampTime) + ((dt - ramping) * to) / rampTime);
  return [Math.min(step, remaining), to];
}

/**
 * The point below which a ramp age counts as spent.
 *
 * Mirrors `cooldownEpsilon` in `internal/game/combat.go` and exists for the same
 * reason: `dt` is not representable in binary floating point, so an age that is a
 * whole number of frames long lands on ~1e-16 rather than 0.
 */
const RAMP_EPSILON = 1e-9;

export interface PredictionInput {
  /** Seconds since the last frame. */
  dt: number;
  /** Movement speed in world units per second, from WELCOME. */
  speed: number;
  /**
   * How long a walk takes to reach `speed`, and to come back to rest, in seconds —
   * `WalkRampTime` from WELCOME.
   *
   * Passed per frame rather than held in the constructor for the same reason `speed`
   * is: the number belongs to the server, the first frame can precede WELCOME, and a
   * local copy is a second source of truth for a value the two halves must share
   * exactly. Zero means the server has the ramp off, and the walk launches at full
   * speed.
   */
  rampTime: number;
  /** The authoritative position from the newest snapshot, if any. */
  server?: Point;
  /**
   * True when the server has acknowledged the client's latest move intent.
   *
   * Until it has, its position reflects an older intent, and reconciling against
   * it would drag the player backwards along a path they have already abandoned.
   */
  acknowledged: boolean;
  /** True when the player is dead or otherwise not self-propelled. */
  frozen: boolean;
  /**
   * True while a stun or a root is holding the feet — `held` on the snapshot. Phase 32.4.
   *
   * **Distinct from `frozen`, and the difference is who owns the position.** A dead player is
   * placed entirely by the server and their route is thrown away; a *held* player is still
   * walking to somewhere, they simply are not getting there yet. So this stops the integration
   * and keeps the destination, exactly as `stepPlayer` stops the feet by reading `CanMove`
   * rather than by clearing `MoveTarget` — and the walk resumes on both halves when the hold
   * ends, with neither having been told anything more.
   */
  held?: boolean;
  /**
   * Movement multiplier from a slow — `moveScale` on the snapshot. Undefined means ×1.
   *
   * Applied where the server applies it, to the speed handed to the ramp, so a slowed walk still
   * accelerates and brakes on the same curve rather than becoming a different kind of movement.
   */
  moveScale?: number;
}

/**
 * Replicates the server's movement for the local player and folds in corrections.
 *
 * The prediction rule here must match `stepPlayer` in
 * `grind/internal/game/movement.go`: advance toward the target at `speed * dt`,
 * snapping on arrival, then confine to walkable floor. If the two drift apart, the
 * player will feel a constant tug as reconciliation fights the prediction.
 */
export class PlayerPredictor {
  /** Where the client believes it is. */
  private position: Point = { x: 0, z: 0 };
  /** Where it is heading, or null when stationary. */
  private target: Point | null = null;
  private started = false;

  /**
   * The dodge roll in progress: where it is heading, how fast, and how long is left.
   *
   * Predicted for the same reason walking is, only more so. A roll covers four units
   * in 0.3 s — about thirteen units per second against a four-and-a-half unit walk —
   * and `RECONCILE_SNAP_DISTANCE` is 3.5, so a roll the client did not predict would
   * be a **full snap on every single press**. That is strictly worse than having no
   * dodge at all.
   *
   * It predicts cleanly because it is the one movement the player does not steer: the
   * direction is fixed when the key goes down, so both halves integrate the same
   * straight line from the same start and cannot diverge except at a wall.
   *
   * **Only the movement is predicted.** Whether a hit landed during the i-frames is an
   * outcome, and outcomes are the server's — the client never decides it took no
   * damage. See `internal/game/dodge.go`.
   */
  private dodgeTarget: Point | null = null;
  private dodgeSpeed = 0;
  private dodgeRemaining = 0;

  /**
   * A predicted dash: where the server said it is taking us, and how long it has left.
   *
   * Separate from the roll's state rather than reusing it, because the two can be told apart in
   * every other way the client cares about — a roll has i-frames and its own animation, a dash is a
   * skill's wind-up — and merging them would make `cancelDodge` end a dash.
   */
  private dashTarget: Point | null = null;
  private dashRemaining = 0;

  /**
   * How far into the acceleration ramp the walk is, in **seconds**, in
   * `[0, rampTime]`. The mirror of `Player.WalkRamp` in `internal/game/entity.go`.
   *
   * A *scalar*, deliberately, and not a velocity vector — which is what makes
   * reconciliation safe. Movement is always toward `target`, so a snap that replaces
   * the position can never leave the player sliding off along a heading they had
   * abandoned; the worst a stale ramp can do is a wrong speed toward a destination,
   * and `reconcile` zeroes it on the snap for that.
   */
  private walkRamp = 0;

  /** Sequence number of the most recent local intent. */
  private seq = 0;

  /**
   * `walkable(to, from)`: whether a step from `from` to `to` is allowed — the zone's rule
   * (`canStep` in `game/zone/layout.ts`), which refuses going deeper into a wall's margin.
   */
  constructor(private readonly walkable: (p: Point, from: Point) => boolean) {}

  /** The position to render and to point the camera at. */
  get current(): Point {
    return this.position;
  }

  /** The sequence number to tag the next CMD_MOVE_TO with. */
  nextSeq(): number {
    this.seq += 1;
    return this.seq;
  }

  /** The latest issued sequence number. */
  get latestSeq(): number {
    return this.seq;
  }

  /** Places the player without prediction — on spawn, or after a zone change. */
  reset(at: Point): void {
    this.position = { x: at.x, z: at.z };
    this.target = null;
    // A teleport lands standing still — the server sets `Moving = false` on every one
    // of them (spawn, respawn, a zone change, a station) and zeroes the ramp with it.
    this.walkRamp = 0;
    // The roll's vector was a point in the zone that was just left. Finishing it here
    // would carry the player four units from the new entry in a direction nobody
    // chose — the same reason `LoadZone` calls `cancelDodge` server-side.
    this.cancelDodge();
    this.started = true;
  }

  /** Begins predicting a walk to a point. Called the instant the click happens. */
  moveTo(target: Point): void {
    this.target = { x: target.x, z: target.z };
  }

  /** Stops predicting movement — an attack order, or arrival. */
  stop(): void {
    this.target = null;
  }

  /**
   * True while a walk is being predicted.
   *
   * Exposed for `Navigator`, which needs to know when a walk has *ended* — a route can
   * be exhausted with the last leg still being walked, so "no waypoints left" and "not
   * going anywhere" are different questions. An arrival intent that could not tell them
   * apart would fire the moment the last leg was issued.
   */
  get walking(): boolean {
    return this.target !== null;
  }

  /** True while a roll is being predicted. */
  get dodging(): boolean {
    return this.dodgeRemaining > 0;
  }

  /**
   * Begins predicting a roll along a direction.
   *
   * **The mirror of `dodge` in `grind/internal/game/dodge.go`**, and the two are one
   * rule written twice in exactly the way `tryMove` and `stepToward` are. `direction`
   * need not be normalised; `distance` and `duration` come from WELCOME, so the client
   * cannot choose either — it is replicating the server's numbers, not picking its own.
   *
   * Optimistic, like every other prediction here: the server may refuse the roll for
   * cost or cooldown, in which case its position will not move and reconciliation
   * pulls the player back. That is the right way round — a refused roll costs a
   * correction, a *granted* roll the client failed to predict costs a snap, and rolls
   * are granted far more often than refused.
   *
   * Returns false when there is no direction to roll in, matching the server's own
   * refusal rather than inventing one.
   */
  dodge(direction: Point, distance: number, duration: number): boolean {
    const length = Math.hypot(direction.x, direction.z);
    if (!(length > 0) || !(distance > 0) || !(duration > 0)) return false;

    this.dodgeSpeed = distance / duration;
    this.dodgeRemaining = duration;
    this.dodgeTarget = {
      x: this.position.x + (direction.x / length) * distance,
      z: this.position.z + (direction.z / length) * distance,
    };
    // A roll abandons the walk, as it does on the server: leaving the target alive
    // would have both integrating at once and the player travelling along the sum.
    this.target = null;
    return true;
  }

  /**
   * Begins a predicted dash toward where the server says it is taking us.
   *
   * **Driven by `EVENT_SKILL_CAST_STARTED`, not by the cast command.** The client cannot work the
   * destination out for itself: the distance is catalogue content and the duration is the
   * character's own attack-speed-scaled wind-up for two of the eight dashes. So the server sends
   * both and this follows them — the same rule the rest of this client obeys about not being a
   * second answer to a question the simulation has already answered.
   *
   * That means it starts a round-trip late, and that is fine and deliberate: the server has moved
   * perhaps a metre by then, far inside `RECONCILE_SNAP_DISTANCE`, so the correction is a smooth
   * catch-up rather than the 6 m snap that not predicting at all would cost.
   */
  dash(to: Point, duration: number): boolean {
    if (!(duration > 0)) return false;
    this.dashTarget = { x: to.x, z: to.z };
    this.dashRemaining = duration;
    // A dash abandons the walk, exactly as a roll does — otherwise both integrate and the body
    // travels along the sum of the two.
    this.target = null;
    return true;
  }

  /** Ends a predicted dash — death, a zone change, or the server saying it is over. */
  cancelDash(): void {
    this.dashTarget = null;
    this.dashRemaining = 0;
  }

  /** Ends a predicted roll — death, or a zone change. */
  cancelDodge(): void {
    this.dodgeTarget = null;
    this.dodgeRemaining = 0;
  }

  /**
   * Advances one frame and returns the position to render.
   *
   * Order matters: predict first, then reconcile. Reconciling first would correct
   * against a position the prediction is about to move away from.
   */
  step(input: PredictionInput): Point {
    if (!this.started) {
      if (input.server) this.reset(input.server);
      return this.position;
    }

    if (input.frozen) {
      this.target = null;
      // Death cancels the roll, as it does server-side. Without this a frozen
      // prediction resumes the roll from wherever the player respawned.
      this.cancelDodge();
      // Reborn standing still: `CMD_RESPAWN` zeroes the ramp server-side too, and a
      // corpse that kept its walk velocity would leave the respawn point moving.
      this.walkRamp = 0;
      // A frozen player is entirely the server's to place.
      if (input.server) this.position = { x: input.server.x, z: input.server.z };
      return this.position;
    }

    this.advance(input.dt, input.speed, input.rampTime, input.held ?? false, input.moveScale ?? 1);
    if (input.server && input.acknowledged) this.reconcile(input.server, input.dt);

    return this.position;
  }

  /** The server's movement rule, replicated. */
  private advance(dt: number, speed: number, rampTime: number, held: boolean, moveScale: number): void {
    if (dt <= 0) return;

    // **The walk ramp winds down whenever the player is not walking.** The same
    // condition `stepPlayer` applies in internal/game/movement.go, and the same three
    // states share it: mid-roll, holding an attack target (which is what nulls the
    // target here, via `Navigator.stop`), and standing still.
    //
    // Winding down rather than resetting is why a ramp is tolerable in a click-to-move
    // game: a player re-clicks constantly, and relaunching from a standstill on every
    // order would make the ramp *slower* than no ramp at all.
    //
    // It is also the whole answer to what a roll does to walk velocity — a roll is not
    // walking, so the ramp unwinds through it, and `WalkRampTime` is at most
    // `DodgeDuration`, so a roll always ends at a standstill.
    // `held` joins the condition rather than unwinding inside its own branch below, so the ramp
    // is spent exactly once per frame — the server unwinds it once too, either here or at its own
    // hold check, never both. Spending it is what makes the walk resume as a fresh launch when
    // the hold ends instead of lurching off at the speed it was held at.
    if (this.dodgeRemaining > 0 || this.dashTarget || held || !this.target || speed <= 0) {
      this.walkRamp = Math.max(0, this.walkRamp - dt);
    }

    // A roll is the whole of the frame: no walking, no steering, exactly as
    // `stepPlayer` returns early on `p.Dodging()`. Checked first so a move order that
    // somehow survived cannot be integrated alongside it. Its speed is never ramped —
    // `dodgeSpeed` is `distance / duration` and stays that on every frame of the roll.
    if (this.dodgeRemaining > 0) {
      this.advanceDodge(dt);
      return;
    }

    // **A hold stops the feet and keeps the destination.** Phase 32.4, mirroring `stepPlayer`,
    // which reads `CanMove` rather than clearing `MoveTarget` — so `this.target` survives and both
    // halves resume the same walk when the stun expires, without the server sending anything.
    //
    // **After the roll and before the dash, which is where the server puts it too.** A roll in
    // flight outlives a stun on both sides: `controlPlayer` cancels the cast and the swing and
    // never the roll, and `stepPlayer` returns on `p.Dodging()` before it ever asks about CC. A
    // *dash* does not outlive one — hard CC cancels the cast that launched it, and `cancelCast`
    // cancels the displacement with it — so the client has to drop it here or it flies the
    // remaining metres of a 6 m dash the server abandoned, which is precisely the snap this phase
    // exists to close.
    if (held) {
      this.cancelDash();
      return;
    }

    // A dash is the whole of the frame too, and for the same reason: the server is carrying the
    // body along a fixed vector, so steering it here would integrate two movements at once.
    // Checked after the roll because a roll begun during a dash is the player cancelling it.
    if (this.dashTarget) {
      this.advanceDash(dt);
      return;
    }

    if (!this.target || speed <= 0) return;

    // Scaled by any slow, at the same place the server scales it — into the ramp, so a chilled
    // walk still accelerates and brakes on the identical curve instead of becoming a different
    // kind of movement. Absent on the wire means ×1, resolved by the caller.
    const remaining = Math.hypot(this.target.x - this.position.x, this.target.z - this.position.z);
    const [step, next] = walkRampStep(this.walkRamp, dt, speed * moveScale, remaining, rampTime);
    this.walkRamp = next;

    if (this.stepToward(this.target, step)) {
      this.target = null;
      // Giving up the destination spends the ramp, for both reasons it can be given up.
      // Arriving is the brake's own conclusion, and being blocked is rock — which does
      // not slow you down, it stops you. `stepPlayer` does exactly this on
      // `arrived || blocked`.
      this.walkRamp = 0;
    }
  }

  /**
   * One frame of a roll. The mirror of `stepDodge` in `internal/game/dodge.go`.
   *
   * The timer is what ends it, not the distance — a roll that only ended on arrival
   * would run forever against a wall, and the body would be stuck in the roll
   * animation unable to act. Blocked or arrived ends it early; the server does the
   * same, and neither takes the i-frames back for it.
   */
  /**
   * One frame of a dash. The mirror of `stepDash` in `internal/game/skills.go`.
   *
   * The speed is recomputed from what is left of the distance over what is left of the window,
   * which is what the server does — so a client that started late, or drifted, converges on the
   * destination at the right moment instead of arriving early and waiting.
   */
  private advanceDash(dt: number): void {
    if (!this.dashTarget) return;
    const remaining = Math.max(dt, this.dashRemaining);
    const distance = Math.hypot(this.dashTarget.x - this.position.x, this.dashTarget.z - this.position.z);
    const finished = this.stepToward(this.dashTarget, (distance / remaining) * dt);

    this.dashRemaining = Math.max(0, this.dashRemaining - dt);
    if (finished || this.dashRemaining <= 0) this.cancelDash();
  }

  private advanceDodge(dt: number): void {
    const finished = this.dodgeTarget ? this.stepToward(this.dodgeTarget, this.dodgeSpeed * dt) : true;

    this.dodgeRemaining = Math.max(0, this.dodgeRemaining - dt);
    if (finished) this.cancelDodge();
  }

  /**
   * Moves up to `step` units toward a point, refusing to enter a wall.
   *
   * Returns true when the destination should be given up — reached, or genuinely
   * blocked. The caller decides *which* destination that is, which is what lets a walk
   * and a roll share one rule; the server's `stepToward` reports the two separately
   * because Go can return a pair.
   */
  private stepToward(destination: Point, step: number): boolean {
    const dx = destination.x - this.position.x;
    const dz = destination.z - this.position.z;
    const distance = Math.hypot(dx, dz);

    if (distance <= step || distance === 0) {
      this.tryMove(destination.x, destination.z);
      return true;
    }

    const scale = step / distance;
    return !this.tryMove(this.position.x + dx * scale, this.position.z + dz * scale);
  }

  /**
   * Moves only onto walkable floor.
   *
   * The client knows the zone layout, so it can refuse a step into a wall exactly
   * as the server will. Without this the prediction would walk through walls and
   * then be yanked back, which is worse than not predicting at all.
   */
  private tryMove(x: number, z: number): boolean {
    if (this.walkable({ x, z }, this.position)) {
      this.position.x = x;
      this.position.z = z;
      return true;
    }

    // Try each axis alone, so sliding along a wall still works instead of
    // stopping dead against it.
    //
    // **A slide that moves nowhere is not a slide.** Walking straight at a wall
    // leaves one axis unchanged, so that axis is trivially walkable — the position
    // it proposes is the one already occupied. Accepting it would keep the target
    // alive with nothing happening, so the player would stand at the wall walking
    // on the spot until they clicked elsewhere. `stepToward` in
    // internal/game/movement.go applies the identical rule; the two must agree.
    if (x !== this.position.x && this.walkable({ x, z: this.position.z }, this.position)) {
      this.position.x = x;
      return true;
    }
    if (z !== this.position.z && this.walkable({ x: this.position.x, z }, this.position)) {
      this.position.z = z;
      return true;
    }

    // Genuinely blocked. The caller gives up the destination rather than pushing at
    // it — a walk drops its target, a roll ends.
    return false;
  }

  /** Folds the authoritative position back in. */
  private reconcile(server: Point, dt: number): void {
    const error = Math.hypot(server.x - this.position.x, server.z - this.position.z);
    if (error <= RECONCILE_TOLERANCE) return;

    if (error >= RECONCILE_SNAP_DISTANCE) {
      this.position.x = server.x;
      this.position.z = server.z;
      this.target = null;
      // **A correction has to settle the velocity, not only the position.** Every snap
      // in this game is a teleport the client could not have predicted — a spawn, a
      // respawn, a zone change, a station — or a stop the client did not know about, and
      // all of them land standing still. Leaving the ramp at full would have the first
      // frame after the snap move at walking pace.
      //
      // Zero is the right settle value rather than the server's own ramp, and the server
      // deliberately does not send it: its ramp lags the client's by a round trip during
      // every launch, so adopting it would drag the predicted speed backwards in time —
      // the opposite of what prediction is for. What makes zero *safe* is that the ramp
      // is a scalar and never a heading: there is no direction left over to slide off
      // along, which is the failure a velocity vector would have had here.
      this.walkRamp = 0;
      return;
    }

    // Small errors are absorbed over a few frames so a correction reads as drift
    // rather than a twitch.
    const alpha = 1 - Math.exp(-RECONCILE_RATE * dt);
    this.position.x += (server.x - this.position.x) * alpha;
    this.position.z += (server.z - this.position.z) * alpha;
  }
}
